import React, { useEffect, memo, useState } from 'react';
import {
  Modal, Form, Input, Select, Button, Space,
  Row, Col, Divider, Spin, Popconfirm, message, Result,
} from 'antd';
import {
  UserOutlined, MailOutlined, PhoneOutlined, SaveOutlined,
  EditOutlined, UserAddOutlined, LinkOutlined, DisconnectOutlined,
  BankOutlined, CheckCircleOutlined,
} from '@ant-design/icons';
import ImageUploader from '../../Ui/ImageUploader';
import { useClinics } from '../../../hooks/useClinics';
import drEasyApi from '../../../Api/Customapi';

const { Option } = Select;
const { TextArea } = Input;

const clinicRelationApis = {
  getByDoctor: async (doctorId) => {
    const res = await drEasyApi.get('/api/Clinic/doctor-clinic-relations', {
      params: { DoctorId: doctorId, PageNumber: 1, PageSize: 100 },
    });
    return res.data.relations;
  },
  link: async (doctorId, clinicId) =>
    drEasyApi.post('/api/Clinic/link-doctor-to-clinic', { doctorId, clinicId }),
  unlink: async (doctorId, clinicId) =>
    drEasyApi.delete('/api/Clinic/unlink-doctor-from-clinic', {
      params: { doctorId, clinicId },
    }),
};

// ============================================================================
// STEP 2 — CLINIC LINKING MODAL (shown after successful add)
// ============================================================================
const ClinicLinkingModal = memo(({ visible, onDone, doctorId, doctorName }) => {
  const { clinicsQuery } = useClinics({ pageNumber: 1, pageSize: 100, staleTime: Infinity });

  const [linkedClinics, setLinkedClinics]       = useState([]);
  const [selectedClinicId, setSelectedClinicId] = useState(null);
  const [clinicsLoading, setClinicsLoading]     = useState(false);
  const [linkLoading, setLinkLoading]           = useState(false);
  const [unlinkLoadingId, setUnlinkLoadingId]   = useState(null);

  const allClinics       = clinicsQuery.data?.clinics || [];
  const linkedIds        = new Set(linkedClinics.map((c) => c.clinicId));
  const availableClinics = allClinics.filter((c) => !linkedIds.has(c.clinicId));

  const fetchLinkedClinics = async () => {
    if (!doctorId) return;
    setClinicsLoading(true);
    try {
      const data = await clinicRelationApis.getByDoctor(doctorId);
      const list = Array.isArray(data) ? data : [];
      setLinkedClinics(list);
    } catch {
      setLinkedClinics([]);
    } finally {
      setClinicsLoading(false);
    }
  };

  useEffect(() => {
    if (visible && doctorId) {
      setLinkedClinics([]);
      setSelectedClinicId(null);
      fetchLinkedClinics();
    }
  }, [visible, doctorId]);

  const handleLink = async () => {
    if (!selectedClinicId || !doctorId) return;
    setLinkLoading(true);
    try {
      await clinicRelationApis.link(doctorId, selectedClinicId);
      setSelectedClinicId(null);
      await fetchLinkedClinics();
      message.success('Clinic linked successfully');
    } catch (e) {
      message.error(e?.response?.data?.[0] || 'Failed to link clinic');
    } finally {
      setLinkLoading(false);
    }
  };

  const handleUnlink = async (clinicId) => {
    if (!doctorId) return;
    setUnlinkLoadingId(clinicId);
    try {
      const response = await clinicRelationApis.unlink(doctorId, clinicId);
      await fetchLinkedClinics();
      message.success(response?.data?.message || 'Clinic unlinked successfully');
    } catch (e) {
      message.error(e?.response?.data?.[0] || 'Failed to unlink clinic');
    } finally {
      setUnlinkLoadingId(null);
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onDone}
      footer={null}
      width={500}
      closable={false}
      maskClosable={false}
      title={
        <div className="flex items-center gap-2">
          <BankOutlined className="text-primary" />
          <span>Link Clinics <span className="text-gray-400 font-normal text-sm">(Optional)</span></span>
        </div>
      }
    >
      {/* ── Success banner ── */}
      <div className="flex items-center gap-3 bg-primary/10 -50 border border-green-200 rounded-lg px-4 py-3 mb-5">
        <div>
          <p className="text-primary -600 text-xs mb-0">
           Do you want to link dr. <span className="font-medium">{doctorName}</span>  with any clinics?
          </p>
        </div>
      </div>

      {/* ── Link new clinic ── */}
      <div className="flex gap-2 mb-4">
        <Select
          className="flex-1"
          placeholder="Select a clinic to link..."
          value={selectedClinicId}
          onChange={setSelectedClinicId}
          showSearch
          optionFilterProp="children"
          disabled={clinicsLoading}
          notFoundContent={
            availableClinics.length === 0
              ? <span className="text-gray-400 text-xs">All clinics already linked</span>
              : null
          }
        >
          {availableClinics.map((c) => (
            <Option key={c.clinicId} value={c.clinicId}>{c.clinicName}</Option>
          ))}
        </Select>
        <Button
          icon={<LinkOutlined />}
          loading={linkLoading}
          disabled={!selectedClinicId}
          onClick={handleLink}
          className="bg-primary text-white border-0 hover:bg-primary_hover"
        >
          Link
        </Button>
      </div>

      {/* ── Linked clinics list ── */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 min-h-[80px]">
        {clinicsLoading ? (
          <div className="flex justify-center py-4"><Spin size="small" /></div>
        ) : linkedClinics.length === 0 ? (
          <p className="text-center text-gray-400 text-sm py-3">No clinics linked yet</p>
        ) : (
          <div className="flex flex-col gap-2">
            {linkedClinics.map((clinic) => (
              <div
                key={clinic.clinicId}
                className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-3 py-2"
              >
                <span className="flex items-center gap-2 text-sm text-gray-700">
                  <BankOutlined className="text-primary" />
                  {clinic.clinicName}
                </span>
                <Popconfirm
                  title={
                    clinic.workingDaysCount > 0
                      ? `Unlinking will remove ${clinic.workingDaysCount} working days. Are you sure?`
                      : 'Are you sure you want to unlink this clinic?'
                  }
                  onConfirm={() => handleUnlink(clinic.clinicId)}
                  okText="Yes"
                  cancelText="No"
                  okButtonProps={{ danger: true }}
                >
                  <Button
                    size="small"
                    danger
                    icon={<DisconnectOutlined />}
                    loading={unlinkLoadingId === clinic.clinicId}
                  >
                    Unlink
                  </Button>
                </Popconfirm>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Footer ── */}
      <div className="flex justify-end mt-5">
        <Button
          type="primary"
          onClick={onDone}
          className="bg-primary border-0 hover:bg-primary_hover"
        >
          {linkedClinics.length > 0 ? 'Done' : 'Skip for now'}
        </Button>
      </div>
    </Modal>
  );
});

ClinicLinkingModal.displayName = 'ClinicLinkingModal';

// ============================================================================
// CONSULTANT FORM MODAL
// ============================================================================
export const ConsultantFormModal = memo(({
  visible, onCancel, onSubmit, item, loading, specializations,
}) => {
  const [form] = Form.useForm();
  const { clinicsQuery } = useClinics({ pageNumber: 1, pageSize: 100, staleTime: Infinity });

  const [linkedClinics, setLinkedClinics]       = useState([]);
  const [selectedClinicId, setSelectedClinicId] = useState(null);
  const [clinicsLoading, setClinicsLoading]     = useState(false);
  const [linkLoading, setLinkLoading]           = useState(false);
  const [unlinkLoadingId, setUnlinkLoadingId]   = useState(null);

  // Step 2 state
  const [step2Visible, setStep2Visible]   = useState(false);
  const [newDoctorId, setNewDoctorId]     = useState(null);
  const [newDoctorName, setNewDoctorName] = useState('');

  const allClinics       = clinicsQuery.data?.clinics || [];
  const linkedIds        = new Set(linkedClinics.map((c) => c.clinicId));
  const availableClinics = allClinics.filter((c) => !linkedIds.has(c.clinicId));

  const fetchLinkedClinics = async (doctorId) => {
    setClinicsLoading(true);
    try {
      const data = await clinicRelationApis.getByDoctor(doctorId);
      const list = Array.isArray(data) ? data : [];
      setLinkedClinics(list);
    } catch {
      setLinkedClinics([]);
    } finally {
      setClinicsLoading(false);
    }
  };

  useEffect(() => {
    if (!visible) return;
    if (item) {
      const firstName   = item.name?.split(' ')[0] || '';
      const lastName    = item.name?.split(' ').slice(1).join(' ') || '';
      const firstNameAr = item.nameAr?.trim().split(' ')[0] || '';
      const lastNameAr  = item.nameAr?.trim().split(' ').slice(1).join(' ') || '';

      form.setFieldsValue({
        ...item,
        firstName, lastName, firstNameAr, lastNameAr,
        email:            item.email || '',
        phoneNumber:      item.phoneNumber || item.phone || '',
        coast:            item.coast || 0,
        specializationId: item.specializationId || '',
        about:            item.about || '',
        aboutAr:          item.aboutAr || '',
        facebook:         item.facebook || '',
        instagram:        item.instagram || '',
        twitter:          item.twitter || '',
        youtube:          item.youtube || '',
        linkedIn:         item.linkedIn || '',
        photoID:          item.photoId || '',
      });
      fetchLinkedClinics(item.doctorId);
    } else {
      form.resetFields();
      setLinkedClinics([]);
      setSelectedClinicId(null);
    }
  }, [visible, item, form]);

  const handleLink = async () => {
    if (!selectedClinicId || !item?.doctorId) return;
    setLinkLoading(true);
    try {
      await clinicRelationApis.link(item.doctorId, selectedClinicId);
      setSelectedClinicId(null);
      await fetchLinkedClinics(item.doctorId);
      message.success('Clinic linked successfully');
    } catch (e) {
      message.error(e?.response?.data?.[0] || 'Failed to link clinic');
    } finally {
      setLinkLoading(false);
    }
  };

  const handleUnlink = async (clinicId) => {
    if (!item?.doctorId) return;
    setUnlinkLoadingId(clinicId);
    try {
      const response = await clinicRelationApis.unlink(item.doctorId, clinicId);
      await fetchLinkedClinics(item.doctorId);
      message.success(response?.data?.message || 'Clinic unlinked successfully');
    } catch (e) {
      message.error(e?.response?.data?.[0] || 'Failed to unlink clinic');
    } finally {
      setUnlinkLoadingId(null);
    }
  };

  const handleSubmit = async (values) => {
    try {
      const data = {
        ...values,
        nameAr:    `${values.firstNameAr || ''} ${values.lastNameAr || ''}`.trim(),
        facebook:  values.facebook  || '',
        instagram: values.instagram || '',
        twitter:   values.twitter   || '',
        youtube:   values.youtube   || '',
        linkedIn:  values.linkedIn  || '',
      };
      if (item) data.id = item.doctorId;

      const result = await onSubmit(data); // 👈 onSubmit must return the created doctor

      form.resetFields();

      // ── If adding (not editing), open Step 2 ──
      if (!item) {
        // Adapt based on what onSubmit returns — e.g. result?.doctorId or result?.id
        const createdId   = result?.doctorId ?? result?.id ?? null;
        const createdName = `${values.firstName} ${values.lastName}`.trim();
        setNewDoctorId(createdId);
        setNewDoctorName(createdName);
        setStep2Visible(true);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const handleStep2Done = () => {
    setStep2Visible(false);
    setNewDoctorId(null);
    setNewDoctorName('');
  };

  return (
    <>
      <Modal
        title={
          <div className="flex items-center">
            {item ? <EditOutlined className="mr-2 text-primary" /> : <UserAddOutlined className="mr-2 text-primary" />}
            {item ? 'Edit Consultant' : 'Add New Consultant'}
          </div>
        }
        open={visible}
        onCancel={onCancel}
        footer={null}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} className="mt-4">

          {/* ── Names ── */}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="firstName" label="First Name (English)"
                rules={[{ required: true, message: 'Please input first name!' }]}>
                <Input prefix={<UserOutlined />} placeholder="Enter first name" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="lastName" label="Last Name (English)"
                rules={[{ required: true, message: 'Please input last name!' }]}>
                <Input prefix={<UserOutlined />} placeholder="Enter last name" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="firstNameAr" label="First Name (Arabic)"
                rules={[{ required: true, message: 'Please input Arabic first name!' }]}>
                <Input prefix={<UserOutlined />} placeholder="أدخل الاسم الأول" dir="rtl" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="lastNameAr" label="Last Name (Arabic)"
                rules={[{ required: true, message: 'Please input Arabic last name!' }]}>
                <Input prefix={<UserOutlined />} placeholder="أدخل اسم العائلة" dir="rtl" />
              </Form.Item>
            </Col>
          </Row>

          {/* ── Cost & Specialization ── */}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="coast" label="Doctor Cost">
                <Input prefix="EGP" type="number" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="specializationId" label="Specialization"
                rules={[{ required: true, message: 'Please select specialization!' }]}>
                <Select placeholder="Select specialization" showSearch>
                  {specializations.map((spec) => (
                    <Option key={spec.id} value={spec.id}>{spec.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* ── Clinics (edit mode only) ── */}
          {item && (
            <>
              <span className="flex items-start justify-start mb-1 gap-1 text-sm">Clinics</span>
              <div className="bg-primary/10 border border-gray-200 rounded-lg p-4 mb-4">
                <div className="flex gap-2 mb-3">
                  <Select
                    className="flex-1"
                    placeholder="Select a clinic to link..."
                    value={selectedClinicId}
                    onChange={setSelectedClinicId}
                    showSearch
                    optionFilterProp="children"
                    disabled={clinicsLoading}
                    notFoundContent={
                      availableClinics.length === 0
                        ? <span className="text-gray-400 text-xs">All clinics already linked</span>
                        : null
                    }
                  >
                    {availableClinics.map((c) => (
                      <Option key={c.clinicId} value={c.clinicId}>{c.clinicName}</Option>
                    ))}
                  </Select>
                  <Button
                    icon={<LinkOutlined />}
                    loading={linkLoading}
                    disabled={!selectedClinicId}
                    onClick={handleLink}
                    className="bg-primary text-white border-0 hover:bg-primary_hover"
                  >
                    Link
                  </Button>
                </div>
                {clinicsLoading ? (
                  <div className="flex justify-center py-4"><Spin size="small" /></div>
                ) : linkedClinics.length === 0 ? (
                  <p className="text-center text-gray-400 text-sm py-2">No clinics linked yet</p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {linkedClinics.map((clinic) => (
                      <div key={clinic.clinicId}
                        className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-3 py-2">
                        <span className="flex items-center gap-2 text-sm text-gray-700">
                          <BankOutlined className="text-primary" />
                          {clinic.clinicName}
                        </span>
                        <Popconfirm
                          title={
                            clinic.workingDaysCount > 0
                              ? `Unlinking will remove ${clinic.workingDaysCount} working days. Are you sure?`
                              : 'Are you sure you want to unlink this clinic?'
                          }
                          onConfirm={() => handleUnlink(clinic.clinicId)}
                          okText="Yes"
                          cancelText="No"
                          okButtonProps={{ danger: true }}
                        >
                          <Button size="small" danger icon={<DisconnectOutlined />}
                            loading={unlinkLoadingId === clinic.clinicId}>
                            Unlink
                          </Button>
                        </Popconfirm>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── Email & Phone ── */}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="email" label="Email"
                rules={[
                  { required: true, message: 'Please input email!' },
                  { type: 'email', message: 'Please enter a valid email!' },
                ]}>
                <Input prefix={<MailOutlined />} placeholder="Enter email" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="phoneNumber" label="Phone Number"
                rules={[{ required: true, message: 'Please input phone number!' }]}>
                <Input prefix={<PhoneOutlined />} placeholder="Enter phone number" />
              </Form.Item>
            </Col>
          </Row>

          {/* ── Password (add mode only) ── */}
          {!item && (
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="password" label="Password"
                  rules={[
                    { required: true, message: 'Please input password!' },
                    {
                      pattern: /^(?=.*[A-Za-z])(?=.*\d).{8,}$/,
                      message: 'Password must be at least 8 characters long and contain both letters and numbers!',
                    },
                  ]}>
                  <Input.Password placeholder="Enter password" />
                </Form.Item>
              </Col>
            </Row>
          )}

          {/* ── About ── */}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="about" label="About"
                rules={[{ required: true, message: 'Please add about consultant!' }]}>
                <TextArea rows={2} placeholder="Enter consultant's description..." />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="aboutAr" label="About (In Arabic)"
                rules={[{ required: true, message: 'Please add about consultant!' }]}>
                <TextArea rows={2} placeholder="...ادخل الوصف باللغة العربية" dir="rtl" />
              </Form.Item>
            </Col>
          </Row>

          {/* ── Photo ── */}
          <Form.Item name="photoID" label="Profile Photo">
            <ImageUploader form={form} spec={false} />
          </Form.Item>

          {/* ── Social Media ── */}
          <Divider orientation="left">Social Media (Optional)</Divider>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="facebook" label="Facebook">
                <Input placeholder="Facebook profile URL" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="instagram" label="Instagram">
                <Input placeholder="Instagram profile URL" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="twitter" label="Twitter">
                <Input placeholder="Twitter profile URL" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="linkedIn" label="LinkedIn">
                <Input placeholder="LinkedIn profile URL" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="youtube" label="YouTube">
            <Input placeholder="YouTube channel URL" />
          </Form.Item>

          {/* ── Footer ── */}
          <Form.Item className="mb-0 mt-6">
            <Space className="w-full justify-end">
              <Button onClick={onCancel}>Cancel</Button>
              <Button
                htmlType="submit"
                loading={loading}
                icon={<SaveOutlined />}
                className="bg-primary text-white hover:bg-primary_hover"
              >
                {item ? 'Update Consultant' : 'Add Consultant'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Step 2: Clinic linking (add mode only) ── */}
      <ClinicLinkingModal
        visible={step2Visible}
        onDone={handleStep2Done}
        doctorId={newDoctorId}
        doctorName={newDoctorName}
      />
    </>
  );
});

ConsultantFormModal.displayName = 'ConsultantFormModal';
// ============================================================================
// SPECIALIST FORM MODAL
// ============================================================================

export const SpecialistFormModal = memo(({
  visible,
  onCancel,
  onSubmit,
  item,
  loading,
  specializations,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible) {
      if (item) {
        const firstName = item.firstName || item.name?.split(' ')[0] || '';
        const lastName = item.lastName || item.name?.split(' ').slice(1).join(' ') || '';

        form.setFieldsValue({
          ...item,
          firstName,
          lastName,
          firstNameAr: item.firstNameAr || '',
          lastNameAr: item.lastNameAr || '',
          email: item.email || '',
          phoneNumber: item.phoneNumber || item.phone || '',
          coast: item.coast || '',
          specializationId: item.specializationId || '',
          about: item.about || '',
          aboutAr: item.aboutAr || '',
          facebook: item.facebook || '',
          instagram: item.instagram || '',
          twitter: item.twitter || '',
          youtube: item.youtube || '',
          linkedIn: item.linkedIn || '',
          photoId: item.photoId || '', // ImageUploader بيقرأ منه مباشرة (spec=true → photoId)
        });
      } else {
        form.resetFields();
      }
    }
  }, [visible, item, form]);

  const handleSubmit = async (values) => {
    try {
      const data = {
        ...values,
        nameAr: `${values.firstNameAr || ''} ${values.lastNameAr || ''}`.trim(),
        facebook: values.facebook || '',
        instagram: values.instagram || '',
        twitter: values.twitter || '',
        youtube: values.youtube || '',
        linkedIn: values.linkedIn || '',
      };

      if (item) {
        data.id = item.specialistId;
      }

      await onSubmit(data);
      form.resetFields();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  return (
    <Modal
      title={
        <div className="flex items-center">
          {item ? (
            <EditOutlined className="mr-2 text-blue-500" />
          ) : (
            <UserAddOutlined className="mr-2 text-blue-500" />
          )}
          {item ? 'Edit Specialist' : 'Add New Specialist'}
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit} className="mt-4">
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="firstName"
              label="First Name (English)"
              rules={[{ required: true, message: 'Please input first name!' }]}
            >
              <Input prefix={<UserOutlined />} placeholder="Enter first name" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="lastName"
              label="Last Name (English)"
              rules={[{ required: true, message: 'Please input last name!' }]}
            >
              <Input prefix={<UserOutlined />} placeholder="Enter last name" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="firstNameAr"
              label="First Name (Arabic)"
              rules={[{ required: true, message: 'Please input Arabic first name!' }]}
            >
              <Input prefix={<UserOutlined />} placeholder="أدخل الاسم الأول" dir="rtl" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="lastNameAr"
              label="Last Name (Arabic)"
              rules={[{ required: true, message: 'Please input Arabic last name!' }]}
            >
              <Input prefix={<UserOutlined />} placeholder="أدخل اسم العائلة" dir="rtl" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="specializationId"
              label="Specialization"
              rules={[{ required: true, message: 'Please select specialization!' }]}
            >
              <Select placeholder="Select specialization" showSearch>
                {specializations.map((spec) => (
                  <Option key={spec.id} value={spec.id}>
                    {spec.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="coast" label="Specialist Cost">
              <Input prefix="EGP" type="number" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Please input email!' },
                { type: 'email', message: 'Please enter a valid email!' },
              ]}
            >
              <Input prefix={<MailOutlined />} placeholder="Enter email" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="phoneNumber"
              label="Phone Number"
              rules={[{ required: true, message: 'Please input phone number!' }]}
            >
              <Input prefix={<PhoneOutlined />} placeholder="Enter phone number" />
            </Form.Item>
          </Col>
        </Row>

        {!item && (
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="password"
                label="Password"
                rules={[
                  { required: true, message: 'Please input password!' },
                  {
                    pattern: /^(?=.*[A-Za-z])(?=.*\d).{8,}$/,
                    message: 'Password must be at least 8 characters long and contain both letters and numbers!',
                  },
                ]}
              >
                <Input.Password placeholder="Enter password" />
              </Form.Item>
            </Col>
          </Row>
        )}

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="about"
              label="About"
              rules={[{ required: true, message: 'Please add about specialist!' }]}
            >
              <TextArea rows={2} placeholder="Enter specialist's description..." />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="aboutAr"
              label="About (In Arabic)"
              rules={[{ required: true, message: 'Please add about specialist!' }]}
            >
              <TextArea rows={2} placeholder="...ادخل الوصف باللغة العربية" dir="rtl" />
            </Form.Item>
          </Col>
        </Row>

        {/* ImageUploader بيحدّث photoId في الـ form تلقائياً عند الرفع */}
        <Form.Item name="photoId" label="Profile Photo">
          <ImageUploader form={form} spec={true} />
        </Form.Item>

        <Divider orientation="left">Social Media (Optional)</Divider>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="facebook" label="Facebook">
              <Input placeholder="Facebook profile URL" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="instagram" label="Instagram">
              <Input placeholder="Instagram profile URL" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="twitter" label="Twitter">
              <Input placeholder="Twitter profile URL" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="linkedIn" label="LinkedIn">
              <Input placeholder="LinkedIn profile URL" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="youtube" label="YouTube">
          <Input placeholder="YouTube channel URL" />
        </Form.Item>

        <Form.Item className="mb-0 mt-6">
          <Space className="w-full justify-end">
            <Button onClick={onCancel}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<SaveOutlined />}
              className="bg-blue-500 hover:bg-blue-600"
            >
              {item ? 'Update Specialist' : 'Add Specialist'}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
});

SpecialistFormModal.displayName = 'SpecialistFormModal';